import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogPrimaryShortcut,
} from './ui/dialog';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';

interface TierLimitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  limitType?: 'project' | 'document' | 'resource' | 'storage';
}

export const TierLimitModal: React.FC<TierLimitModalProps> = ({ open, onOpenChange, limitType = 'project' }) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { token } = useAuth();
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (open && token && !hasTrackedRef.current) {
      hasTrackedRef.current = true;
      fetch(`${API_BASE}/api/observability-events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          eventType: 'tier_limit_modal_shown',
          userMessage: `${t('tier_limit_modal_shown') || 'Tier limit modal shown'} - ${limitType}`,
        }),
      }).catch((error) => {
        console.error('Error tracking tier limit modal shown:', error);
      });
    }
    if (!open) {
      hasTrackedRef.current = false;
    }
  }, [open, token, limitType, t]);

  const handleUpgrade = (): void => {
    onOpenChange(false);
    navigate('/desk/usage-and-billing');
  };

  const getTitle = (): string => {
    if (limitType === 'project') {
      return t('project_limit_reached') || 'Project Limit Reached';
    }
    if (limitType === 'document') {
      return t('document_limit_reached') || 'Document Limit Reached';
    }
    if (limitType === 'storage') {
      return t('storage_limit_reached') || 'Storage Limit Reached';
    }
    return t('resource_limit_reached') || 'Resource Limit Reached';
  };

  const getDescription = (): string => {
    if (limitType === 'project') {
      return t('project_limit_reached_message') || "You've reached the project limit. Please upgrade your plan to create more projects.";
    }
    if (limitType === 'document') {
      return t('document_limit_reached_message') || "You've reached the document limit for this project. Please upgrade your plan to create more documents.";
    }
    if (limitType === 'storage') {
      return t('storage_limit_reached_message') || "You've reached your storage quota. Please upgrade your plan to upload more files.";
    }
    return t('resource_limit_reached_message') || "You've reached the resource limit for this project. Please upgrade your plan to add more resources.";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription>
            {getDescription()}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <CancelButton onClick={() => onOpenChange(false)}>
            {t('cancel') || 'Cancel'}
          </CancelButton>
          <Button dialogPrimary onClick={handleUpgrade} className="inline-flex items-center gap-1.5">
            {t('upgrade_plan') || 'Upgrade plan'}
            <DialogPrimaryShortcut className="ml-0.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

